import { Fragment, useEffect, useState } from "react";
import PropTypes from "prop-types";
import ColorPicker from "./ColorPicker.jsx";
import AccountPicker from "./AccountPicker.jsx";
import Modal from "./Modal.jsx";
import { useConfig } from "../configContext.js";

const FIELD =
  "w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500";
const BTN = "rounded-md px-2.5 py-1.5 text-sm font-medium disabled:opacity-50";
const BTN_SOLID = `${BTN} bg-brand-500 text-white hover:bg-brand-600`;
const BTN_SOFT = `${BTN} border border-gray-300 text-gray-700 hover:bg-gray-50`;
const CELL = "px-3 py-2 text-sm text-gray-700";

export default function GroupManager({
  onClose,
  groups,
  setGroups,
  setMessage,
  setColor,
  setSnackbarOpen,
}) {
  const { apiUrl } = useConfig();
  const [tab, setTab] = useState(0);
  // null until the first answer: "loading" is derived from that, rather than a
  // second flag the effect would have to set by hand.
  const [distributionLists, setDistributionLists] = useState(null);
  const [importingDl, setImportingDl] = useState(null);

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [newGroupMembers, setNewGroupMembers] = useState([]);

  const [editingGroupId, setEditingGroupId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editMembers, setEditMembers] = useState([]);

  const notify = (message, color) => {
    setMessage(message);
    setColor(color);
    setSnackbarOpen(true);
  };

  const fail = (err) => notify("Erreur : " + err.message, "danger");

  /** Groups come back with every mutation; a failure only raises a message. */
  const handleApiResponse = (data) => {
    if (data.success) setGroups(data.groups);
    else notify(data.message, "danger");
    return data.success;
  };

  // Distribution lists are fetched once, when their tab is first opened.
  useEffect(() => {
    if (tab !== 1 || distributionLists !== null) return undefined;
    let cancelled = false;
    fetch(`${apiUrl}/distribution-lists`, { credentials: "include" })
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled) setDistributionLists(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        console.error(err);
        // Answered, even if badly: the tab must stop saying "loading".
        if (!cancelled) setDistributionLists([]);
      });
    return () => {
      cancelled = true;
    };
  }, [tab, apiUrl, distributionLists]);

  const dlLoading = tab === 1 && distributionLists === null;
  const lists = distributionLists || [];

  const createGroup = async () => {
    if (!newGroupName.trim()) return notify("Le nom du groupe est requis.", "danger");
    try {
      const res = await fetch(`${apiUrl}/groups`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: newGroupName,
          members: newGroupMembers.map((m) => ({ name: m.name, uuid: m.uuid })),
        }),
      });
      if (!handleApiResponse(await res.json())) return undefined;
      setNewGroupName("");
      setNewGroupMembers([]);
      setShowCreateForm(false);
      notify("Groupe créé avec succès", "success");
    } catch (err) {
      fail(err);
    }
    return undefined;
  };

  const deleteGroup = async (groupId) => {
    try {
      const res = await fetch(`${apiUrl}/groups/${groupId}`, { method: "DELETE" });
      if (handleApiResponse(await res.json())) notify("Groupe supprimé", "success");
    } catch (err) {
      fail(err);
    }
  };

  const changeGroupColor = async (groupId, colorObj) => {
    try {
      const res = await fetch(`${apiUrl}/groups/${groupId}/color`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ color: `${colorObj.r},${colorObj.g},${colorObj.b}` }),
      });
      handleApiResponse(await res.json());
    } catch (err) {
      fail(err);
    }
  };

  const syncGroup = async (groupId) => {
    try {
      const res = await fetch(`${apiUrl}/groups/${groupId}/sync`, { method: "POST" });
      if (handleApiResponse(await res.json())) notify("Groupe synchronisé", "success");
    } catch (err) {
      fail(err);
    }
  };

  const startEdit = (group) => {
    setEditingGroupId(group.id);
    setEditName(group.name);
    setEditMembers(
      (group.members || []).map((m) => ({ name: m.account_name, uuid: m.account_uuid })),
    );
  };

  const cancelEdit = () => {
    setEditingGroupId(null);
    setEditName("");
    setEditMembers([]);
  };

  const saveEdit = async () => {
    try {
      await fetch(`${apiUrl}/groups/${editingGroupId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: editName }),
      });
      const res = await fetch(`${apiUrl}/groups/${editingGroupId}/members`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          members: editMembers.map((m) => ({ name: m.name, uuid: m.uuid })),
        }),
      });
      if (!handleApiResponse(await res.json())) return;
      setEditingGroupId(null);
      notify("Groupe mis à jour", "success");
    } catch (err) {
      fail(err);
    }
  };

  const importDl = async (dlName) => {
    setImportingDl(dlName);
    try {
      const res = await fetch(`${apiUrl}/groups/from-dl`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ dl_name: dlName }),
      });
      if (handleApiResponse(await res.json())) notify(`Liste « ${dlName} » importée`, "success");
    } catch (err) {
      fail(err);
    } finally {
      setImportingDl(null);
    }
  };

  return (
    <Modal title="Gérer les groupes" onClose={onClose} width="max-w-4xl">
      <div role="tablist" className="mb-4 flex gap-1 border-b border-gray-200">
        <TabButton active={tab === 0} onClick={() => setTab(0)}>Mes groupes</TabButton>
        <TabButton active={tab === 1} onClick={() => setTab(1)}>Listes de distribution</TabButton>
      </div>

      {tab === 0 && (
        <div>
          {!showCreateForm ? (
            <button type="button" className={`${BTN_SOLID} mb-4`} onClick={() => setShowCreateForm(true)}>
              Créer un groupe
            </button>
          ) : (
            <div className="mb-4 rounded-md bg-gray-50 p-3">
              <h3 className="mb-2 text-sm font-semibold text-gray-700">Nouveau groupe</h3>
              <input
                value={newGroupName}
                onChange={(event) => setNewGroupName(event.target.value)}
                placeholder="Nom du groupe"
                aria-label="Nom du groupe"
                className={`${FIELD} mb-2`}
              />
              <div className="mb-2">
                <AccountPicker
                  value={newGroupMembers}
                  onChange={setNewGroupMembers}
                  placeholder="Rechercher des comptes"
                />
              </div>
              <div className="flex gap-2">
                <button type="button" className={BTN_SOLID} onClick={createGroup}>Créer</button>
                <button
                  type="button"
                  className={BTN_SOFT}
                  onClick={() => {
                    setShowCreateForm(false);
                    setNewGroupName("");
                    setNewGroupMembers([]);
                  }}
                >
                  Annuler
                </button>
              </div>
            </div>
          )}

          {groups.length === 0 ? (
            <p className="text-sm text-gray-500">Aucun groupe.</p>
          ) : (
            <table className="w-full table-fixed">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-500">
                  <th className="w-[34%] px-3 py-2">Nom</th>
                  <th className="w-[12%] px-3 py-2">Type</th>
                  <th className="w-[12%] px-3 py-2">Couleur</th>
                  <th className="w-[12%] px-3 py-2">Membres</th>
                  <th className="w-[30%] px-3 py-2 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((group) => (
                  <Fragment key={group.id}>
                    <tr className="border-b border-gray-100 hover:bg-gray-50">
                      <td className={CELL}>
                        {editingGroupId === group.id ? (
                          <input
                            value={editName}
                            onChange={(event) => setEditName(event.target.value)}
                            aria-label="Nom du groupe"
                            className={FIELD}
                          />
                        ) : (
                          <span className="truncate" title={group.name}>{group.name}</span>
                        )}
                      </td>
                      <td className={CELL}>
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                          {group.type === "zimbra_dl" ? "DL" : "Manuel"}
                        </span>
                      </td>
                      <td className={CELL}>
                        <ColorPicker
                          background={group.color}
                          updateAccount={changeGroupColor}
                          accountId={group.id}
                        />
                      </td>
                      <td className={CELL}>{(group.members || []).length}</td>
                      <td className={`${CELL} text-center`}>
                        <div className="flex justify-center gap-1">
                          {editingGroupId === group.id ? (
                            <>
                              <IconButton label="Enregistrer" tone="success" onClick={saveEdit} path="M5 13l4 4L19 7" />
                              <IconButton label="Annuler" onClick={cancelEdit} path="M6 18L18 6M6 6l12 12" />
                            </>
                          ) : (
                            <>
                              <IconButton
                                label="Modifier"
                                onClick={() => startEdit(group)}
                                path="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                              />
                              {group.type === "zimbra_dl" && (
                                <IconButton
                                  label="Synchroniser"
                                  tone="brand"
                                  onClick={() => syncGroup(group.id)}
                                  path="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                />
                              )}
                              <IconButton
                                label="Supprimer"
                                tone="danger"
                                onClick={() => deleteGroup(group.id)}
                                path="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                    {editingGroupId === group.id && (
                      <tr className="border-b border-gray-100">
                        <td colSpan={5} className="px-3 pb-3">
                          <span className="mb-1 block text-xs text-gray-500">Membres :</span>
                          <AccountPicker
                            value={editMembers}
                            onChange={setEditMembers}
                            placeholder="Rechercher des comptes"
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === 1 && (
        <div>
          {dlLoading && <p className="text-sm text-gray-500">Chargement…</p>}
          {!dlLoading && lists.length === 0 && (
            <p className="text-sm text-gray-500">Aucune liste de distribution trouvée.</p>
          )}
          {!dlLoading && lists.length > 0 && (
            <table className="w-full table-fixed">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-500">
                  <th className="w-[55%] px-3 py-2">Nom</th>
                  <th className="w-[15%] px-3 py-2 text-center">Membres</th>
                  <th className="w-[30%] px-3 py-2 text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {lists.map((dl) => {
                  const alreadyImported = groups.some(
                    (g) => g.type === "zimbra_dl" && g.zimbra_dl_name === dl.name,
                  );
                  return (
                    <tr key={dl.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className={CELL}>
                        <span className="truncate" title={dl.name}>{dl.name}</span>
                      </td>
                      <td className={`${CELL} text-center`}>{dl.member_count ?? "—"}</td>
                      <td className={`${CELL} text-center`}>
                        {alreadyImported ? (
                          <span className="rounded-full border border-gray-300 px-2 py-0.5 text-xs text-gray-500">
                            Déjà importée
                          </span>
                        ) : (
                          <button
                            type="button"
                            className={BTN_SOFT}
                            disabled={importingDl !== null}
                            onClick={() => importDl(dl.name)}
                          >
                            {importingDl === dl.name ? "Import…" : "Importer"}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </Modal>
  );
}

GroupManager.propTypes = {
  onClose: PropTypes.func.isRequired,
  groups: PropTypes.array.isRequired,
  setGroups: PropTypes.func.isRequired,
  setMessage: PropTypes.func.isRequired,
  setColor: PropTypes.func.isRequired,
  setSnackbarOpen: PropTypes.func.isRequired,
};

function TabButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${
        active
          ? "border-brand-500 text-brand-700"
          : "border-transparent text-gray-500 hover:text-gray-700"
      }`}
    >
      {children}
    </button>
  );
}

TabButton.propTypes = {
  active: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  children: PropTypes.node,
};

const TONES = {
  neutral: "border-gray-300 text-gray-600 hover:bg-gray-50",
  brand: "border-brand-300 text-brand-700 hover:bg-brand-50",
  success: "border-green-300 text-green-700 hover:bg-green-50",
  danger: "border-red-300 text-red-700 hover:bg-red-50",
};

function IconButton({ label, onClick, path, tone = "neutral" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className={`flex h-7 w-7 items-center justify-center rounded border ${TONES[tone]}`}
    >
      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d={path} />
      </svg>
    </button>
  );
}

IconButton.propTypes = {
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  path: PropTypes.string.isRequired,
  tone: PropTypes.oneOf(["neutral", "brand", "success", "danger"]),
};
